import * as React from 'react';

import {
  Badge,
  Button,
  cn,
  Col,
  MaybeTruncatedList,
  Menu,
  Row,
  Tooltip,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import startCase from 'lodash/startCase';
import {
  LucideMoreHorizontal,
  LucidePlusCircle,
  LucideTrash2,
  LucideUsers,
} from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';

import * as conversationState from '../_state/conversation.state';
import * as conversationListState from '../_state/conversationList.state';

import { NewParticipantPopover } from './NewParticipantPopover';

type HeaderTabProps = {
  children: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
};

const HeaderTab = ({ children, selected, onClick }: HeaderTabProps) => (
  <button
    className={cn(
      '@HeaderTab',
      'border-y-3 border-transparent py-2 text-sm font-medium text-neutral/50 capitalize outline-none',
      'hover:text-neutral',
      selected && 'border-b-neutral text-neutral',
    )}
    onClick={onClick}
  >
    {children}
  </button>
);

export const ConversationHeader = () => {
  const selectedConversation = useAtomValue(
    conversationListState.$selectedConversation,
  );

  const [activeTab, setActiveTab] = useAtom(conversationState.$activeTab);

  const deleteConversation = useSetAtom(conversationState.$deleteConversation);
  const updateConversationSubject = useSetAtom(
    conversationState.$updateConversationSubject,
  );

  const linkedObjects = React.useMemo(
    () => selectedConversation?.custom.objectsData ?? [],
    [selectedConversation],
  );

  const handleDeleteConversation = () => {
    if (!selectedConversation?.id) {
      return;
    }

    deleteConversation(selectedConversation?.id);
  };

  const [tempSubject, setTempSubject] = React.useState(
    selectedConversation?.subject ?? '',
  );
  const [isEditingSubject, setIsEditingSubject] = React.useState(false);

  const handleStartEditingSubject = () => {
    setTempSubject(selectedConversation?.subject ?? '');
    setIsEditingSubject(true);
  };

  const handleSaveSubject = () => {
    updateConversationSubject(tempSubject);
    setIsEditingSubject(false);
  };

  return (
    <Col className="@ConversationHeader light rounded-[10px] border border-neutral/20 bg-neutral/7 px-3 pt-2.5 pr-2.5">
      <Row gap2 yStart>
        <Col className={'w-full gap-0.5'}>
          <Col gap1 className={'w-full'}>
            <Row gap1>
              <Badge
                className={'-ml-0.5 self-start'}
                color={
                  selectedConversation?.custom.conversationType === 'external'
                    ? 'warning'
                    : 'primary'
                }
              >
                {startCase(selectedConversation?.custom.conversationType)}
              </Badge>
              {linkedObjects.map((object) => (
                <Badge key={object.id}>{object.title}</Badge>
              ))}
            </Row>
            <div className={'flex w-full'}>
              {!isEditingSubject && (
                <div
                  className={
                    'mr-1 -ml-1 w-0 grow truncate rounded px-1 py-0.5 text-lg font-medium transition-all hover:bg-current/7'
                  }
                  onClick={handleStartEditingSubject}
                >
                  {selectedConversation?.subject || 'Untitled Conversation'}
                </div>
              )}
              {isEditingSubject && (
                <input
                  type="text"
                  value={tempSubject}
                  autoFocus={true}
                  placeholder="Enter subject..."
                  className="-ml-1 w-full rounded border-none bg-current/7 px-1 py-0.5 text-lg font-medium outline-none"
                  onBlur={handleSaveSubject}
                  onChange={(e) => setTempSubject(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveSubject();
                    }
                    if (e.key === 'Escape') {
                      setIsEditingSubject(false);
                    }
                  }}
                />
              )}
            </div>
          </Col>
          <Row gap2>
            <LucideUsers className={'size-4 shrink-0 opacity-50'} />
            {/* 🟢🟢🟢 LIST OF PARTICIPANTS */}
            <div className={'w-0 grow'}>
              <MaybeTruncatedList
                items={selectedConversation?.participants ?? []}
                renderItem={(item, index) => (
                  <span
                    key={index}
                    className={'text-sm whitespace-nowrap text-neutral/50'}
                  >
                    {index > 0 && ', '}
                    {item.user?.name}
                  </span>
                )}
                renderPlus={(collapsedItems) => (
                  <Row gap1>
                    {collapsedItems.length > 0 && (
                      <Tooltip
                        placement="bottom"
                        tip={
                          <div className={'max-w-[200px]'}>
                            {collapsedItems
                              .map((item) => item.user?.name)
                              .join(', ')}
                          </div>
                        }
                      >
                        <span
                          className={
                            'rounded bg-current/10 px-1 py-0.5 text-sm whitespace-nowrap text-neutral/50'
                          }
                        >
                          +{collapsedItems.length}
                        </span>
                      </Tooltip>
                    )}
                    <NewParticipantPopover>
                      <Row>
                        <AriaButton
                          className={cn(
                            '-my-1 cursor-pointer rounded-full border-none bg-transparent p-1.5 opacity-50 transition-all outline-none',
                            'hover:bg-primary/15 hover:text-primary hover:opacity-100',
                          )}
                        >
                          <LucidePlusCircle className={'size-4'} />
                        </AriaButton>
                      </Row>
                    </NewParticipantPopover>
                  </Row>
                )}
              />
            </div>
          </Row>
        </Col>
        <Menu
          trigger={
            <Button
              icon={<LucideMoreHorizontal />}
              variant="outline"
              size="icon"
              className={'ml-auto'}
            />
          }
          items={[
            {
              label: 'Delete Conversation',
              onAction: handleDeleteConversation,
              icon: <LucideTrash2 />,
            },
          ]}
        ></Menu>
      </Row>
      <Row gap4>
        {conversationState.conversationTabs.map((tab) => (
          <HeaderTab
            key={tab}
            selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </HeaderTab>
        ))}
      </Row>
    </Col>
  );
};
